import { AUTH_TYPES, TEMPLATE_CATEGORIES } from './constants.js'

/*
 * Zoho Desk ticket notifier for privacy request events.
 *
 * POSTs to /api/v1/tickets on the tenant's Zoho Desk data centre (desk.zoho.<dc>). Zoho requires an
 * orgId header and OAuth2. Ticket creation mandates departmentId and contactId, which vary per data
 * subject and can't be resolved from the event feed alone, so this template uses a fixed,
 * admin-configured department and a generic privacy contact ([DEPARTMENT_ID]/[CONTACT_ID]), with the
 * request detail in the subject/description. The data-centre suffix ([dc] = com, eu, in, com.au, ...)
 * and org id are admin-replaced at setup.
 */
export const zohoDeskTemplate = {
	id: 'zoho-desk',
	guid: '21543f6d-a3e8-4fa6-b1c0-40c355d844b5',
	name: 'Zoho Desk',
	category: TEMPLATE_CATEGORIES.SUPPORT,
	popularity: 'Popular',
	color: '#e42527',
	icon: 'mdi-face-agent',
	description: 'Create Zoho Desk tickets when privacy requests are raised or approach and breach their statutory deadlines.',
	features: ['OAuth2 Auth', 'Ticket Management', 'Priority Mapping', 'Deadline Escalation'],
	endpoints: [
		{
			name: 'Create Ticket - New Privacy Request',
			url: 'https://desk.zoho.[dc]/api/v1/tickets',
			method: 'POST',
			description: 'Create a Zoho Desk ticket when a new privacy request is submitted. Replace [dc] with your data-centre suffix and set [DEPARTMENT_ID]/[CONTACT_ID].',
			cassieObjectID: -13,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'RequestTypeName', filter: '' },
				{ name: 'StatusName', filter: '' }
			],
			requestBody: '{ "subject": "New Privacy Request [PLACEHOLDER_REQUESTNUMBER]", "description": "Privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]) of type [PLACEHOLDER_REQUESTTYPENAME] was submitted. Current status: [PLACEHOLDER_STATUSNAME].", "departmentId": "[DEPARTMENT_ID]", "contactId": "[CONTACT_ID]", "priority": "Low" }',
			headers: [
				{ Name: 'orgId', value: '[ORG_ID]' },
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 200, message: '' }]
		},
		{
			name: 'Create Ticket - Request/Task Nearing Deadline',
			url: 'https://desk.zoho.[dc]/api/v1/tickets',
			method: 'POST',
			description: 'Create a Zoho Desk ticket when a privacy request or task enters its warning window.',
			cassieObjectID: -16,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'Deadline', filter: '' },
				{ name: 'DaysUntilDue', filter: '' }
			],
			requestBody: '{ "subject": "[PLACEHOLDER_EVENTTITLE] - Request [PLACEHOLDER_REQUESTNUMBER]", "description": "[PLACEHOLDER_EVENTTITLE] for privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]). Deadline [PLACEHOLDER_DEADLINE]; days until due: [PLACEHOLDER_DAYSUNTILDUE].", "departmentId": "[DEPARTMENT_ID]", "contactId": "[CONTACT_ID]", "priority": "Medium" }',
			headers: [
				{ Name: 'orgId', value: '[ORG_ID]' },
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 200, message: '' }]
		},
		{
			name: 'Create Ticket - Deadline Breached',
			url: 'https://desk.zoho.[dc]/api/v1/tickets',
			method: 'POST',
			description: 'Create a high-priority Zoho Desk ticket when a privacy request or task passes its statutory deadline.',
			cassieObjectID: -17,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'Deadline', filter: '' },
				{ name: 'DaysOverdue', filter: '' }
			],
			requestBody: '{ "subject": "[PLACEHOLDER_EVENTTITLE] - Request [PLACEHOLDER_REQUESTNUMBER]", "description": "[PLACEHOLDER_EVENTTITLE] for privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]). Deadline was [PLACEHOLDER_DEADLINE]; days overdue: [PLACEHOLDER_DAYSOVERDUE].", "departmentId": "[DEPARTMENT_ID]", "contactId": "[CONTACT_ID]", "priority": "High" }',
			headers: [
				{ Name: 'orgId', value: '[ORG_ID]' },
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 200, message: '' }]
		}
	],
	defaults: {
		endpointAddress: 'https://desk.zoho.[dc]/api/v1',
		authenticationUrl: 'https://accounts.zoho.[dc]/oauth/v2/token',
		authenticationTypeId: AUTH_TYPES.OAUTH2_CLIENT_CREDENTIALS,
		requestThrottlingWaitTimeMS: 1000,
		auth2Scope: 'Desk.tickets.CREATE',
		notes: 'Zoho Desk tickets for privacy request events. OAuth2 self-client; the token endpoint and API host are data-centre specific (replace [dc], e.g. com/eu/in/com.au). Set the orgId header and a fixed [DEPARTMENT_ID]/[CONTACT_ID] (Zoho requires both on create; a generic privacy contact is the simplest route). priority maps breach->High, nearing->Medium, new request->Low. Keep a non-zero throttle.'
	}
}
